import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'motion/react';
import { ShoppingBasket, Sprout } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';

import { signUpSchema, type SignUpInput } from '@sreerasa/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/brand/logo';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/auth/sign-up')({
  validateSearch: z.object({ role: z.enum(['customer', 'farmer']).optional() }),
  component: SignUpPage,
});

function SignUpPage() {
  const { role } = Route.useSearch();
  const navigate = useNavigate();

  const form = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      full_name: '',
      email: '',
      password: '',
      confirm_password: '',
      requested_role: role ?? 'customer',
    },
  });

  const selectedRole = form.watch('requested_role');

  async function onSubmit(values: SignUpInput) {
    const { error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        // This metadata is read by the handle_new_user trigger (migration
        // 0300), which whitelists it down to farmer-or-customer. Asking for
        // 'superadmin' here does nothing — the trigger ignores anything that
        // isn't 'farmer'. That whitelist is the reason this is safe to send
        // from a client at all.
        data: {
          full_name: values.full_name,
          display_name: values.full_name.split(' ')[0],
          requested_role: values.requested_role,
        },
      },
    });

    if (error) {
      form.setError('root', { message: error.message });
      return;
    }

    toast.success('Account created', {
      description:
        values.requested_role === 'farmer'
          ? "Next: add your farm. We'll verify it before your produce goes live."
          : 'Welcome to SreeRasa.',
    });

    void navigate({ to: values.requested_role === 'farmer' ? '/farmer' : '/shop' });
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitions.base}
      >
        <div className="text-center">
          <Link to="/" className="inline-block">
            <Logo size="lg" />
          </Link>
          <h1 className="mt-6 font-display text-3xl font-black text-soil-900">Join SreeRasa</h1>
          <p className="mt-2 text-soil-600">Buy direct from farms, or sell your own produce.</p>
        </div>

        {/* ── Role picker ── */}
        <div className="mt-8 grid grid-cols-2 gap-3">
          {(
            [
              {
                value: 'customer',
                icon: ShoppingBasket,
                title: 'I want to buy',
                body: 'Produce, delivered',
              },
              { value: 'farmer', icon: Sprout, title: 'I want to sell', body: 'List my harvest' },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => form.setValue('requested_role', option.value)}
              aria-pressed={selectedRole === option.value}
              className={cn(
                'rounded-card border-2 p-4 text-left transition-all active:scale-98',
                selectedRole === option.value
                  ? 'border-farm-600 bg-farm-50'
                  : 'border-soil-200 hover:border-soil-300',
              )}
            >
              <option.icon
                className={cn(
                  'size-5',
                  selectedRole === option.value ? 'text-farm-600' : 'text-soil-400',
                )}
                aria-hidden
              />
              <p className="mt-2 font-display text-sm font-bold text-soil-900">{option.title}</p>
              <p className="text-xs text-soil-500">{option.body}</p>
            </button>
          ))}
        </div>

        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <Input
            label="Full name"
            autoComplete="name"
            required
            {...form.register('full_name')}
            error={form.formState.errors.full_name?.message}
          />
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            {...form.register('email')}
            error={form.formState.errors.email?.message}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            hint="At least 8 characters"
            {...form.register('password')}
            error={form.formState.errors.password?.message}
          />
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            {...form.register('confirm_password')}
            error={form.formState.errors.confirm_password?.message}
          />

          {form.formState.errors.root && (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm font-medium text-danger">
              {form.formState.errors.root.message}
            </p>
          )}

          <Button
            type="submit"
            size="lg"
            full
            isLoading={form.formState.isSubmitting}
            loadingText="Creating…"
          >
            Create account
          </Button>

          {selectedRole === 'farmer' && (
            <p className="rounded-lg bg-harvest-100 p-3 text-xs leading-relaxed text-harvest-800">
              Farmer accounts need a verified farm before produce can go live. It takes a few days —
              that verification is what makes the promise on every listing mean anything.
            </p>
          )}
        </form>

        <p className="mt-6 text-center text-sm text-soil-600">
          Already have an account?{' '}
          <Link to="/auth/sign-in" className="font-semibold text-farm-700 hover:underline">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
